import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Alert, Button, Card, Col, Container, Form, Row, Table } from 'react-bootstrap'
import { ArrowLeft, Copy, Trash2 } from 'lucide-react'
import {
  fetchProductComparisonTable,
  fetchProducts,
  fetchProductPimData,
  fetchTermReferenceTable,
  deleteTermReference,
  setKingspanCanonicalValue,
  type ProductComparisonRowDto,
  type ProductDataItemDto,
  type ProductDto,
  type TermReferenceRowDto,
} from '../services/api'
import { useToast } from '../components/ToastProvider'
import ProductPimCategoriesPanel from '../features/products/ProductPimCategoriesPanel'

export default function ProductPimManualEditor() {
  const { productId } = useParams<{ productId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [draftValues, setDraftValues] = useState<Record<string, { value: string; unit: string }>>({})
  const [deletedCanonicalIds, setDeletedCanonicalIds] = useState<Set<string>>(new Set())

  const { data: products = [], isLoading: productsLoading } = useQuery<ProductDto[]>({
    queryKey: ['products-for-pim-manual'],
    queryFn: () => fetchProducts(),
  })

  const product = useMemo(
    () => products.find((p) => p.id === productId),
    [products, productId]
  )

  const { data: termReference = [] } = useQuery<TermReferenceRowDto[]>({
    enabled: !!product?.family,
    queryKey: ['term-reference-for-manual', product?.family],
    queryFn: () =>
      fetchTermReferenceTable({
        productFamily: product!.family || undefined,
      }),
  })

  const comparisonQuery = useQuery<ProductComparisonRowDto[]>({
    enabled: !!productId,
    queryKey: ['product-comparison-table-manual', productId],
    queryFn: () => fetchProductComparisonTable(productId!, undefined, false),
  })
  const comparisonRows = comparisonQuery.data ?? []
  const comparisonLoading = comparisonQuery.isPending
  const refetchComparison = comparisonQuery.refetch

  const { data: pimData = [], isLoading: pimLoading } = useQuery<ProductDataItemDto[]>({
    enabled: !!productId,
    queryKey: ['product-pim-manual', productId],
    queryFn: () => fetchProductPimData(productId!),
  })

  useEffect(() => {
    const initial: Record<string, { value: string; unit: string }> = {}
    for (const row of comparisonRows) {
      initial[row.canonicalAttributeId] = {
        value: row.kingspanValue ?? '',
        unit: row.canonicalUnit ?? '',
      }
    }
    setDraftValues(initial)
  }, [comparisonRows])

  const categories = useMemo(() => {
    const set = new Set<string>()
    for (const row of comparisonRows) {
      if (!row.category) continue
      set.add(row.category)
    }
    return Array.from(set).sort()
  }, [comparisonRows])

  const normalizeLookupValue = (value?: string) =>
    (value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '')

  const allowedCanonicalKeys = useMemo(() => {
    const keys = new Set<string>()
    for (const row of termReference) {
      if (!row.category || !row.canonicalTerm) continue
      keys.add(`${row.category}:${row.canonicalTerm}`)
    }
    return keys
  }, [termReference])

  const rowsByCategory = useMemo(() => {
    // First, filter to rows that belong to the current family PIM categories and are not locally deleted.
    const filtered = comparisonRows.filter((row) => {
      if (deletedCanonicalIds.has(row.canonicalAttributeId)) return false
      if (allowedCanonicalKeys.size > 0) {
        const key = `${row.category}:${row.displayLabel}`
        if (!allowedCanonicalKeys.has(key)) return false
      }
      if (selectedCategory === 'all') return true
      return row.category === selectedCategory
    })

    // Then, deduplicate by (category + displayLabel) so we only show one row per PIM field.
    const byKey = new Map<string, ProductComparisonRowDto>()
    for (const row of filtered) {
      const key = `${row.category}:${row.displayLabel}`
      const existing = byKey.get(key)
      if (!existing) {
        byKey.set(key, row)
        continue
      }
      const existingHasValue = !!existing.kingspanValue || !!existing.distributorValue
      const currentHasValue = !!row.kingspanValue || !!row.distributorValue
      if (!existingHasValue && currentHasValue) {
        byKey.set(key, row)
      }
    }
    return Array.from(byKey.values())
  }, [comparisonRows, selectedCategory, allowedCanonicalKeys, deletedCanonicalIds])

  const relatedPimByCanonicalRowKey = useMemo(() => {
    const map = new Map<string, ProductDataItemDto[]>()

    for (const row of comparisonRows) {
      const rowCategory = normalizeLookupValue(row.category)
      const rowNames = new Set([
        normalizeLookupValue(row.attributeName),
        normalizeLookupValue(row.displayLabel),
      ])

      const related = pimData.filter((item) => {
        if (normalizeLookupValue(item.category) !== rowCategory) return false

        const itemNames = [
          normalizeLookupValue(item.attributeName),
          normalizeLookupValue(item.displayLabel),
        ]

        return itemNames.some((name) => name && rowNames.has(name))
      })

      map.set(row.canonicalAttributeId, related)
    }

    return map
  }, [comparisonRows, pimData])

  const extraPimItems = useMemo(() => {
    const extras: ProductDataItemDto[] = []

    // If no family PIM mapping exists yet, show all imported PIM items as extras
    // so users can still confirm their import succeeded.
    if (allowedCanonicalKeys.size === 0) {
      extras.push(...pimData)
      return extras.sort((a, b) => {
        const catCmp = (a.category || '').localeCompare(b.category || '')
        if (catCmp !== 0) return catCmp
        return (a.attributeName || '').localeCompare(b.attributeName || '')
      })
    }

    for (const item of pimData) {
      const key = `${item.category}:${item.displayLabel || item.attributeName}`
      if (allowedCanonicalKeys.has(key)) continue
      extras.push(item)
    }
    return extras.sort((a, b) => {
      const catCmp = (a.category || '').localeCompare(b.category || '')
      if (catCmp !== 0) return catCmp
      return (a.attributeName || '').localeCompare(b.attributeName || '')
    })
  }, [pimData, allowedCanonicalKeys])

  const [saveStatus, setSaveStatus] = useState<string | null>(null)
  const { showToast } = useToast()

  const saveFieldMutation = useMutation({
    mutationFn: async (row: ProductComparisonRowDto) => {
      const draft = draftValues[row.canonicalAttributeId] ?? { value: '', unit: '' }
      await setKingspanCanonicalValue(productId!, row.canonicalAttributeId, {
        value: draft.value,
        unit: draft.unit || undefined,
        actor: 'product-pim-manual-editor',
      })
    },
    onSuccess: async () => {
      setSaveStatus('Field saved.')
      showToast('Field saved.', { title: 'Product data', variant: 'success' })
      await Promise.all([
        refetchComparison(),
        queryClient.invalidateQueries({ queryKey: ['product-pim', productId] }),
      ])
    },
    onError: () => {
      setSaveStatus('Failed to save field.')
      showToast('Failed to save field.', { title: 'Product data', variant: 'danger' })
    },
  })

  const deleteCanonicalAttributeMutation = useMutation({
    mutationFn: (canonicalAttributeId: string) => deleteTermReference(canonicalAttributeId),
    onSuccess: async (_data, canonicalAttributeId) => {
      setSaveStatus('PIM field removed.')
      showToast('PIM field removed.', { title: 'Product data', variant: 'success' })
      // Optimistically hide this field in the current view
      setDeletedCanonicalIds((prev) => {
        const next = new Set(prev)
        next.add(canonicalAttributeId)
        return next
      })
      await Promise.all([
        refetchComparison(),
        queryClient.invalidateQueries({ queryKey: ['term-reference-for-manual', product?.family] }),
        queryClient.invalidateQueries({ queryKey: ['product-pim-manual', productId] }),
      ])
    },
    onError: () => {
      setSaveStatus('Failed to remove PIM field.')
      showToast('Failed to remove PIM field.', { title: 'Product data', variant: 'danger' })
    },
  })

  const handleSaveAll = async () => {
    if (!productId) return
    setSaveStatus(null)
    try {
      for (const row of rowsByCategory) {
        const draft = draftValues[row.canonicalAttributeId]
        if (!draft || !draft.value.trim()) continue
        // eslint-disable-next-line no-await-in-loop
        await setKingspanCanonicalValue(productId, row.canonicalAttributeId, {
          value: draft.value,
          unit: draft.unit || undefined,
          actor: 'product-pim-manual-editor',
        })
      }
      setSaveStatus('All filled fields saved.')
      showToast('All filled fields saved.', { title: 'Product data', variant: 'success' })
      await Promise.all([
        refetchComparison(),
        queryClient.invalidateQueries({ queryKey: ['product-pim', productId] }),
      ])
    } catch {
      setSaveStatus('Failed to save one or more fields.')
      showToast('Failed to save one or more fields.', { title: 'Product data', variant: 'danger' })
    }
  }

  const handleFillFromPim = () => {
    setDraftValues((prev) => {
      const updated = { ...prev }
      for (const row of rowsByCategory) {
        const existing = updated[row.canonicalAttributeId]
        if (existing?.value?.trim()) continue // already has a Kingspan value
        const related = relatedPimByCanonicalRowKey.get(row.canonicalAttributeId) ?? []
        if (related.length === 0) continue
        // Prefer en-GB, then any English, then first available
        const preferred =
          related.find((p) => (p.languageCode || '').toLowerCase() === 'en-gb') ??
          related.find((p) => (p.languageCode || '').toLowerCase().startsWith('en')) ??
          related[0]
        updated[row.canonicalAttributeId] = {
          value: preferred.value ?? '',
          unit: preferred.unit || existing?.unit || '',
        }
      }
      return updated
    })
  }

  const hasLoaded = !productsLoading && !comparisonLoading && !pimLoading

  return (
    <Container className="py-3">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div className="d-flex align-items-center gap-3">
          <Button
            variant="link"
            className="p-0 me-1"
            onClick={() => navigate('/products')}
          >
            <ArrowLeft size={18} /> Back to product library
          </Button>
          {product && (
            <div>
              <div className="text-muted text-uppercase small mb-1">Editing product</div>
              <h1 className="h3 mb-0">
                {product.code} – {product.name}
              </h1>
            </div>
          )}
        </div>
        {product?.family && (
          <div className="text-end small">
            <div className="text-muted">Product family</div>
            <div className="fw-semibold">{product.family}</div>
            <div>
              <Link to="/pim">Open compliance setup (PIM)</Link>
            </div>
          </div>
        )}
      </div>

      <Alert variant="light">
        <strong>Use this page to add or edit Kingspan product data manually.</strong>{' '}
        Shared product fields (category, code, name, country, division, market, family and URL)
        appear at the top. Attributes below come from the comparison-table API and are narrowed using your product
        family&apos;s{' '}
        <strong>compared-attribute</strong> mapping (term reference) —{' '}
        <Link to="/pim">edit under PIM / compliance setup</Link>
        . Use <strong>Data groups for this product</strong> (below) to include/exclude master catalog slices before editing fields.
      </Alert>

      {product && (
        <Card className="mb-3">
          <Card.Body>
            <Row className="g-3">
              <Col md={3}>
                <Form.Label className="small text-muted">Category</Form.Label>
                <div className="fw-semibold">{product.family || 'N/A'}</div>
              </Col>
              <Col md={3}>
                <Form.Label className="small text-muted">Code</Form.Label>
                <div className="fw-semibold">{product.code}</div>
              </Col>
              <Col md={6}>
                <Form.Label className="small text-muted">Name</Form.Label>
                <div className="fw-semibold">{product.name}</div>
              </Col>
            </Row>
            <Row className="g-3 mt-1">
              <Col md={3}>
                <Form.Label className="small text-muted">Country</Form.Label>
                <div>{product.country || 'N/A'}</div>
              </Col>
              <Col md={3}>
                <Form.Label className="small text-muted">Division</Form.Label>
                <div>{product.division || 'N/A'}</div>
              </Col>
              <Col md={3}>
                <Form.Label className="small text-muted">Market</Form.Label>
                <div>{product.market || 'N/A'}</div>
              </Col>
              <Col md={3}>
                <Form.Label className="small text-muted">Kingspan URL</Form.Label>
                {product.kingspanUrl ? (
                  <a href={product.kingspanUrl} target="_blank" rel="noreferrer">
                    {product.kingspanUrl}
                  </a>
                ) : (
                  <div>N/A</div>
                )}
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )
      }

      {product && productId && <ProductPimCategoriesPanel productId={productId} />}

      {hasLoaded && product && comparisonQuery.isError && (
        <Alert variant="danger" className="mb-3">
          <div className="fw-semibold mb-1">Could not load comparison rows</div>
          <div className="small mb-2">
            The manual editor needs{' '}
            <code className="small">GET /api/products/&lt;id&gt;/comparison-table</code> to succeed. If this returns HTTP 500,
            confirm the API database has PIM / AI reconciliation tables (run EF migrations or deploy the latest API startup repair)
            and restart the App Service. Until then the attribute grid stays empty even though imported PIM rows may appear under
            &quot;Other discovered PIM fields&quot;.
          </div>
          <Button size="sm" variant="outline-danger" onClick={() => void comparisonQuery.refetch()}>
            Retry comparison load
          </Button>
        </Alert>
      )}

      {!hasLoaded && <div className="text-muted">Loading product data…</div>}

      {hasLoaded && !product && (
        <Alert variant="danger">Product not found. Return to the product library.</Alert>
      )}

      {hasLoaded && product && (
        <>
          <Card className="mb-3">
            <Card.Body>
              <Row className="g-3 align-items-end">
                <Col md={4}>
                  <Form.Label className="small">Category</Form.Label>
                  <Form.Select
                    size="sm"
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                  >
                    <option value="all">All categories</option>
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md="auto" className="d-flex gap-2">
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    title="Copy PIM values into empty Kingspan value fields (you can still edit before saving)"
                    disabled={saveFieldMutation.isPending}
                    onClick={handleFillFromPim}
                  >
                    <Copy size={14} className="me-1" />
                    Fill from PIM
                  </Button>
                  <Button
                    size="sm"
                    variant="primary"
                    disabled={saveFieldMutation.isPending}
                    onClick={handleSaveAll}
                  >
                    Save all filled fields
                  </Button>
                </Col>
                <Col>
                  <div className="small text-muted">
                    Fields with existing Kingspan values are pre-filled. You can
                    overwrite them or add new values.
                  </div>
                </Col>
              </Row>
              {saveStatus && (
                <div className="mt-2 small text-muted">{saveStatus}</div>
              )}
            </Card.Body>
          </Card>

          <Card>
            <Card.Body>
              {rowsByCategory.length === 0 && (
                <div className="text-muted small">
                  {comparisonQuery.isError ? (
                    <>
                      Comparison data failed to load — fix the API error above, then retry. Imported PIM items may still
                      appear under &quot;Other discovered PIM fields&quot;.
                    </>
                  ) : comparisonRows.length === 0 ? (
                    <>
                      No comparison rows returned yet for this product (canonical Kingspan/distributor facts may still be building).
                      If PIM import succeeded, open{' '}
                      <Link to="/pim">compliance setup (PIM)</Link> for this family so fields appear in the term-reference mapping.
                    </>
                  ) : (
                    <>
                      No attributes match this category filter and current term-reference mapping. Try &quot;All categories&quot;
                      or widen{' '}
                      <Link to="/pim">family data groups &amp; attributes</Link>
                      .
                    </>
                  )}
                </div>
              )}

              {rowsByCategory.length > 0 && (
                <Table size="sm" bordered hover responsive>
                  <thead>
                    <tr>
                      <th style={{ width: '18%' }}>Category</th>
                      <th style={{ width: '22%' }}>Field</th>
                      <th style={{ width: '25%' }}>Kingspan value</th>
                      <th style={{ width: '15%' }}>Unit</th>
                      <th>Existing PIM data (read-only)</th>
                      <th style={{ width: '10%' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rowsByCategory.map((row) => {
                      const draft = draftValues[row.canonicalAttributeId] ?? {
                        value: '',
                        unit: '',
                      }

                      const relatedPim = relatedPimByCanonicalRowKey.get(row.canonicalAttributeId) ?? []

                      return (
                        <tr key={row.canonicalAttributeId}>
                          <td>{row.category}</td>
                          <td>
                            <div className="fw-semibold">{row.displayLabel}</div>
                            <div className="text-muted small">{row.attributeName}</div>
                          </td>
                          <td>
                            <Form.Control
                              size="sm"
                              value={draft.value}
                              onChange={(e) =>
                                setDraftValues((prev) => ({
                                  ...prev,
                                  [row.canonicalAttributeId]: {
                                    ...draft,
                                    value: e.target.value,
                                  },
                                }))
                              }
                            />
                          </td>
                          <td>
                            <Form.Control
                              size="sm"
                              value={draft.unit}
                              placeholder={row.canonicalUnit || ''}
                              onChange={(e) =>
                                setDraftValues((prev) => ({
                                  ...prev,
                                  [row.canonicalAttributeId]: {
                                    ...draft,
                                    unit: e.target.value,
                                  },
                                }))
                              }
                            />
                          </td>
                          <td>
                            {relatedPim.length === 0 && (
                              <span className="text-muted small">No imported PIM values yet.</span>
                            )}
                            {relatedPim.length > 0 && (
                              <ul className="small mb-0 ps-3">
                                {relatedPim.slice(0, 3).map((item) => (
                                  <li key={item.id} className="d-flex align-items-baseline gap-1">
                                    <span>
                                      {item.value}
                                      {item.unit ? ` ${item.unit}` : ''}
                                      <span className="text-muted ms-1">
                                        ({item.source || 'PIM'}, {item.languageCode || 'n/a'})
                                      </span>
                                    </span>
                                    <Button
                                      size="sm"
                                      variant="link"
                                      className="p-0 ms-1 text-secondary"
                                      title="Copy this value into the Kingspan value field"
                                      style={{ fontSize: '0.7rem', lineHeight: 1 }}
                                      onClick={() =>
                                        setDraftValues((prev) => ({
                                          ...prev,
                                          [row.canonicalAttributeId]: {
                                            ...prev[row.canonicalAttributeId],
                                            value: item.value ?? '',
                                            unit: item.unit || prev[row.canonicalAttributeId]?.unit || '',
                                          },
                                        }))
                                      }
                                    >
                                      <Copy size={11} />
                                    </Button>
                                  </li>
                                ))}
                                {relatedPim.length > 3 && (
                                  <li className="text-muted">
                                    +{relatedPim.length - 3} more value(s)
                                  </li>
                                )}
                              </ul>
                            )}
                          </td>
                          <td className="text-center">
                            <div className="d-flex justify-content-center gap-2">
                              <Button
                                size="sm"
                                variant="outline-primary"
                                disabled={saveFieldMutation.isPending || !draft.value.trim()}
                                onClick={() => {
                                  setSaveStatus(null)
                                  saveFieldMutation.mutate(row)
                                }}
                              >
                                Save
                              </Button>
                              <Button
                                size="sm"
                                variant="link"
                                className="p-0 text-danger"
                                title="Delete this PIM field row"
                                disabled={deleteCanonicalAttributeMutation.isPending}
                                onClick={() => {
                                  const ok = window.confirm('Delete this PIM field row (retire canonical term reference)?')
                                  if (!ok) return
                                  deleteCanonicalAttributeMutation.mutate(row.canonicalAttributeId)
                                }}
                              >
                                <Trash2 size={16} />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>

          {extraPimItems.length > 0 && (
            <Card className="mt-3">
              <Card.Body>
                <details>
                  <summary className="small fw-semibold text-muted" style={{ cursor: 'pointer' }}>
                    Other discovered PIM fields ({extraPimItems.length})
                  </summary>
                  <div className="mt-2 small text-muted">
                    These fields were discovered by scrapes or PIM imports but are not yet part of the
                    family master categories. You can update the PIM categories if any of them should
                    become editable Kingspan fields.
                  </div>
                  <div className="mt-2">
                    <Table size="sm" bordered hover responsive>
                      <thead>
                        <tr>
                          <th style={{ width: '18%' }}>Category</th>
                          <th style={{ width: '22%' }}>Field</th>
                          <th>Sample value</th>
                          <th style={{ width: '16%' }}>Source</th>
                        </tr>
                      </thead>
                      <tbody>
                        {extraPimItems.slice(0, 80).map((item) => (
                          <tr key={item.id}>
                            <td>{item.category}</td>
                            <td>
                              <div className="fw-semibold">{item.displayLabel || item.attributeName}</div>
                              {item.attributeName && (
                                <div className="text-muted small">{item.attributeName}</div>
                              )}
                            </td>
                            <td>
                              <span title={item.value}>
                                {String(item.value).length > 80
                                  ? `${String(item.value).slice(0, 80)}…`
                                  : item.value}
                                {item.unit ? ` ${item.unit}` : ''}
                              </span>
                            </td>
                            <td>
                              <div>{item.source || 'Unknown'}</div>
                              {item.sourceUrl && (
                                <div className="text-muted small text-truncate" title={item.sourceUrl}>
                                  {item.sourceUrl}
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </details>
              </Card.Body>
            </Card>
          )}
        </>
      )}
    </Container>
  )
}

